import { Capacitor } from "@capacitor/core";
import { NativeOnDeviceAI } from "./plugins";

export type ChatTurn = { role: "user" | "assistant"; text: string };

export type LocalAIState = {
  hasLocalAI: boolean;
  platform: "ios" | "android" | "web";
  reason?: string;
};

export async function detectLocalAI(): Promise<LocalAIState> {
  const platform = Capacitor.getPlatform() as "ios" | "android" | "web";
  if (!Capacitor.isNativePlatform()) return { hasLocalAI: false, platform: "web", reason: "web" };

  try {
    const availability = await NativeOnDeviceAI.getAvailability();
    return {
      hasLocalAI: availability.available,
      platform,
      reason: availability.reason,
    };
  } catch (error) {
    return { hasLocalAI: false, platform, reason: error instanceof Error ? error.message : "unavailable" };
  }
}

export async function localChatReply(message: string, contextJson: string, history: ChatTurn[] = []) {
  const historyBlock = history
    .slice(-8)
    .map((turn) => `${turn.role === "user" ? "User" : "Assistant"}: ${turn.text}`)
    .join("\n");
  const prompt = [
    "You are Screen0, a calm digital wellbeing assistant.",
    "Answer using ONLY the usage context JSON. Never mention message contents of other apps.",
    `Usage context JSON:\n${contextJson}`,
    historyBlock ? `Recent chat:\n${historyBlock}` : "",
    `User question: ${message}`,
  ].filter(Boolean).join("\n\n");

  const result = await NativeOnDeviceAI.chatReply({ message, contextJson, prompt });
  const reply = result.reply?.trim();
  if (!reply) throw new Error("On-device AI did not return a reply. Try again.");
  return reply;
}

export async function localGenerateInsight(contextJson: string, prompt: string) {
  const result = await NativeOnDeviceAI.generateInsight({ contextJson, prompt });
  const title = result.title?.trim();
  const body = result.body?.trim();
  const actions = (result.actions ?? []).map((action) => action.trim()).filter(Boolean).slice(0, 2);
  if (!title || !body) throw new Error("On-device AI did not return an insight. Try again.");
  return { title, body, actions };
}
